import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import {
    Box, Typography, CircularProgress, Alert, Button, Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PinCard from './PinCard';

const PinCustomerDetail = ({ posConfig }) => {
    const { kdnr } = useParams();
    const navigate = useNavigate();
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const [pins, setPins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [nextToken, setNextToken] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const pinCardRefs = useRef({});

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleWsEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handleWsEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);

            if (msg.task === 'respGetPinsByKdnr') {
                if (msg.status === 'succeeded') {
                    if (msg.nextTokenUsed) {
                        setPins((prev) => [...prev, ...(msg.pins || [])]);
                    } else {
                        setPins(msg.pins || []);
                    }
                    setNextToken(msg.nextToken || null);
                    setError(null);
                } else {
                    setError(msg.message || 'Fehler beim Laden der PINs.');
                }
                setLoading(false);
                setLoadingMore(false);
                return;
            }

            // Forward pin-specific messages to cards
            const fwd = ['respRevealPin', 'respRedeemPin'];
            if (fwd.includes(msg.task)) {
                Object.values(pinCardRefs.current).forEach((h) => {
                    if (typeof h === 'function') h(msg);
                });
            }
        } catch (e) {
            console.error('WS parse error:', e);
        }
    }, []);

    const loadPins = useCallback((opts = {}) => {
        const token = opts.nextToken || null;
        if (token) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setPins([]);
        }
        setNextToken(null);
        sendMessage(JSON.stringify({
            task: 'getPinsByKdnr',
            station: cookies.station,
            staff: cookies.staff,
            kdnr: Number(kdnr),
            nextToken: token || undefined,
        }));
    }, [sendMessage, cookies.station, cookies.staff, kdnr]);

    useEffect(() => {
        if (readyState === ReadyState.OPEN && kdnr) loadPins();
    }, [readyState, kdnr]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoadMore = () => {
        if (nextToken) loadPins({ nextToken });
    };

    return (
        <Box>
            <Paper sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('..')}
                    >
                        ← Zurück
                    </Button>
                    <Typography variant="subtitle2">
                        PINs – Kunde {kdnr}
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : pins.length === 0 ? (
                    <Typography sx={{ py: 6, textAlign: 'center', color: '#999', fontSize: 14 }}>
                        Keine PIN-Records für diesen Kunden.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {pins.map((pin) => (
                                <PinCard
                                    key={pin.pinRecordId}
                                    pin={pin}
                                    sendMessage={sendMessage}
                                    cookies={cookies}
                                    registerMessageHandler={(h) => { pinCardRefs.current[pin.pinRecordId] = h; }}
                                    unregisterMessageHandler={() => { delete pinCardRefs.current[pin.pinRecordId]; }}
                                />
                            ))}
                        </Box>

                        {nextToken && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disableElevation
                                    disabled={loadingMore}
                                    onClick={handleLoadMore}
                                    sx={{
                                        borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600,
                                        borderColor: '#ddd', color: '#1a1a1a',
                                    }}
                                >
                                    {loadingMore ? <CircularProgress size={16} /> : 'Mehr laden'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default PinCustomerDetail;
